<script setup lang="ts">
import { createSeoBreadcrumbList, createSeoWebSite, resolveSeoUrl } from '~/composables/useSeoJsonLd'
import { formatRelativeTime } from '~/utils/appTime'
import {
  getNewsArticleList,
  getNewsBelongTabType,
  getNewsHome,
  getNewsDetailPath,
  searchNewsArticles,
  type NewsCategoryItem,
  type NewsListItem,
  type NewsListResult,
} from '~/api/news'

const NEWS_PAGE_SIZE = 10
const NEWS_SEARCH_QUERY_KEY = 'newsKeyword'
const NEWS_SEARCH_BREADCRUMB_LABEL_KEY = '80338'
const NEWS_CATEGORY_PATHS: Record<string, string> = {
  art_latest: '/news',
  bit_yuanchuang: '/blog',
  bit_yaowen: '/industry',
  bit_jys: '/exchangenews',
  bit_brandcenter: '/press',
  bit_researchcenter: '/research',
}
const NEWS_CATEGORY_BREADCRUMB_LABEL_KEYS: Record<string, string> = {
  art_latest: '85428',
  bit_yuanchuang: '85485',
  bit_yaowen: '85430',
  bit_jys: '80337',
  bit_brandcenter: '85483',
  bit_researchcenter: '85484',
}
const NEWS_CATEGORY_SEO_DESCRIPTION_KEYS: Record<string, string> = {
  art_latest: '84121',
  bit_yuanchuang: '85595',
  bit_yaowen: '85600',
  bit_jys: '85594',
  bit_brandcenter: '85603',
  bit_researchcenter: '85601',
}
/** CollectionPage.name / ItemList.name 取当前页面 Title，与各列表页 useSeoHead 的 title 同 key。 */
const NEWS_CATEGORY_SEO_TITLE_KEYS: Record<string, string> = {
  art_latest: '85598',
  bit_yuanchuang: '85597',
  bit_yaowen: '85602',
  bit_jys: '85596',
  bit_brandcenter: '85605',
  bit_researchcenter: '85604',
}
/** 搜索结果页的 Title（带关键词时使用）。 */
const NEWS_SEARCH_SEO_TITLE_KEY = '84129'

type NewsCategory = string
type NewsCategoryTab = {
  key: NewsCategory
  label: string
  code: string
}
type NewsArticle = {
  id: string
  title: string
  summary: string
  date: string
  tag: string
  image: string
  icon: string
  showname: string
  showtime: string
  wikiTimestamp?: number
  urlPath: string
  belongTabType: number
}

const route = useRoute()
const appStore = useAppStore()
const { localePath, localeMeta, t } = useAppI18n()
const { getBreadcrumb, setBreadcrumb } = useLsBreadcrumb()
const sourceBreadcrumb = ref<{ route: string; label?: string; labelKey?: string } | null>(null)
const newsCategories = ref<NewsCategoryTab[]>([])
const newsListResult = ref<NewsListResult | null>(null)
const searchResult = ref<NewsListResult | null>(null)
const hotNewsResult = ref<NewsListResult | null>(null)
const isNewsSwitching = ref(false)

/** 静态 SEO 页面通过路由元数据指定分类编码。 */
const routeCategory = computed(() => (typeof route.meta.newsCategory === 'string' ? route.meta.newsCategory : ''))
const currentCategory = computed<NewsCategory>(() => {
  return routeCategory.value && newsCategories.value.some((item) => item.key === routeCategory.value)
    ? routeCategory.value
    : newsCategories.value[0]?.key || ''
})
/** 根据当前列表 URL 识别展示用分类，避免分类接口数据影响面包屑文案。 */
const currentRouteCategory = computed<NewsCategory>(() => {
  const matchedCategory = Object.entries(NEWS_CATEGORY_PATHS).find(([, categoryPath]) => {
    const localizedCategoryPath = localePath(categoryPath)

    return route.path === localizedCategoryPath || route.path.startsWith(`${localizedCategoryPath}/`)
  })

  return matchedCategory?.[0] || 'art_latest'
})
/** 根据新闻分类获取对应的面包屑语言包 key，未知分类回退到最新。 */
const getCategoryBreadcrumbLabelKey = (category: NewsCategory) =>
  NEWS_CATEGORY_BREADCRUMB_LABEL_KEYS[category] || NEWS_CATEGORY_BREADCRUMB_LABEL_KEYS.art_latest

// 新闻列表和详情来源面包屑共用当前 URL 对应的语言包 key。
const currentCategoryBreadcrumbLabelKey = computed(() => getCategoryBreadcrumbLabelKey(currentRouteCategory.value))
/** 解析纯数字页码，非法页码统一回到第一页。 */
const currentPage = computed(() => {
  const page = Array.isArray(route.params.page) ? route.params.page[0] : route.params.page
  const parsedPage = Number(page || 1)

  return Number.isFinite(parsedPage) && parsedPage > 0 ? Math.trunc(parsedPage) : 1
})
/** 显式访问 /page/1 时展示 404，避免产生重复的第一页入口。 */
const is404 = computed(() => {
  const page = Array.isArray(route.params.page) ? route.params.page[0] : route.params.page

  return page === '1'
})
/** URL 中的新闻搜索词，与反馈列表相同，路由是唯一数据来源。 */
const currentKeyword = computed(() => {
  const keyword = route.query[NEWS_SEARCH_QUERY_KEY]
  const resolvedKeyword = Array.isArray(keyword) ? keyword[0] : keyword

  return typeof resolvedKeyword === 'string' ? resolvedKeyword.trim() : ''
})
const searchKeyword = ref(currentKeyword.value)
const languageCode = computed(() => (localeMeta.language === 'ar-SA' ? 'ar' : localeMeta.language))
const countryCode = computed(() => appStore.CountryCode || '156')

const normalizeCategory = (category: NewsCategoryItem): NewsCategoryTab | null => {
  if (!category.code) return null

  return {
    key: category.code,
    code: category.code,
    label: category.name || category.code,
  }
}

const homeKey = computed(() => `news-home-${languageCode.value}-${countryCode.value}`)
const { data: homeData } = await useAsyncData(
  homeKey,
  async () => {
    const result = await getNewsHome({ languageId: languageCode.value, countryId: countryCode.value })

    return {
      categories: result.categories.map(normalizeCategory).filter((item): item is NewsCategoryTab => Boolean(item)),
      hot: result.hot,
    }
  },
  { watch: [languageCode, countryCode] },
)

watchEffect(() => {
  newsCategories.value = homeData.value?.categories || []
  hotNewsResult.value = homeData.value?.hot || null
})

const categoryCode = computed(() => {
  const selectedCategory = newsCategories.value.find((item) => item.key === routeCategory.value)

  return selectedCategory?.code || newsCategories.value[0]?.code
})
const articleKey = computed(
  () =>
    `news-articles-${categoryCode.value}-${currentPage.value}-${currentKeyword.value}-${languageCode.value}-${countryCode.value}`,
)
const articleDataPromise = useAsyncData(
  articleKey,
  async () => {
    if (currentKeyword.value) {
      return {
        list: null,
        search: await searchNewsArticles({
          keyword: currentKeyword.value,
          languageId: languageCode.value,
          countryId: countryCode.value,
          pageIndex: currentPage.value,
          pageSize: NEWS_PAGE_SIZE,
        }),
      }
    }

    return {
      list: await getNewsArticleList({
        categoryCode: categoryCode.value,
        languageId: languageCode.value,
        countryId: countryCode.value,
        lasttime: '1970-01-01',
        pageIndex: currentPage.value,
        pageSize: NEWS_PAGE_SIZE,
      }),
      search: null,
    }
  },
  { watch: [() => route.fullPath, languageCode, countryCode] },
)

const { data: articleData, pending: articlePending } = await articleDataPromise

watch(
  articlePending,
  (pending) => {
    if (pending) isNewsSwitching.value = true
    else if (articleData.value) isNewsSwitching.value = false
  },
  { immediate: true },
)

watchEffect(() => {
  newsListResult.value = articleData.value?.list || null
  searchResult.value = articleData.value?.search || null
})

const activeNewsResult = computed(() => (currentKeyword.value ? searchResult.value : newsListResult.value))
const totalArticles = computed(() => activeNewsResult.value?.total || 0)
const toDisplayArticle = (item: NewsListItem, index: number): NewsArticle => ({
  id: item.code || item.artcode || `news-${index}`,
  title: item.title || '',
  summary: currentKeyword.value ? item.summary || '' : item.content || '',
  date: formatRelativeTime(item.showtime || item.wiki_timestamp, languageCode.value, { translate: t }),
  tag: item.tags?.[0]?.tagname || '',
  image: currentKeyword.value ? item.coverImg || '' : item.imgurl || '',
  icon: item.icon || '/images/news/hot-profile.png',
  showname: item.showname || 'WikiBit',
  showtime: item.showtime || '',
  wikiTimestamp: item.wiki_timestamp,
  urlPath: item.urlPath || '',
  belongTabType: getNewsBelongTabType(item),
})
const formatNewsTime = (article: NewsArticle) =>
  formatRelativeTime(article.wikiTimestamp, languageCode.value, { translate: t })
const handleArticleClick = (article: NewsArticle) => {
  if (!article.urlPath || import.meta.server) return

  setBreadcrumb(article.urlPath, {
    // 搜索页统一使用“新闻”语言包，其他场景使用新闻自身的分类。
    labelKey: currentKeyword.value
      ? NEWS_SEARCH_BREADCRUMB_LABEL_KEY
      : getCategoryBreadcrumbLabelKey(currentRouteCategory.value),
    route: route.fullPath,
  })
  const detailUrl = localePath(getNewsDetailPath(article.belongTabType, article.urlPath))
  window.open(detailUrl, '_blank', 'noopener,noreferrer')
}
const pageArticles = computed(() => (activeNewsResult.value?.items || []).map(toDisplayArticle))
const newsPageRoute = computed(() => {
  const categoryPath = NEWS_CATEGORY_PATHS[currentCategory.value] || '/news'
  const query = new URLSearchParams()

  if (currentKeyword.value) {
    query.set(NEWS_SEARCH_QUERY_KEY, currentKeyword.value)
    return `/news/page/{idx}/search?${query.toString()}`
  }

  return `${categoryPath}/page/{idx}`
})

/** 搜索分页点击第 1 页时跳过冗余的 /page/1，保留当前关键词。 */
const handleNewsPaginationClick = (event: MouseEvent) => {
  if (
    !currentKeyword.value ||
    import.meta.server ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    !(event.target instanceof Element)
  ) {
    return
  }

  const link = event.target.closest<HTMLAnchorElement>('a[href]')
  if (!link || !new URL(link.href).pathname.endsWith('/news/page/1/search')) return

  event.preventDefault()
  event.stopPropagation()
  void navigateTo({ path: localePath('/news/search'), query: route.query })
}
const breadcrumbItems = computed(() => {
  const items: Array<{ label: string; route?: string }> = [{ label: t('82520'), route: localePath('/') }]

  if (sourceBreadcrumb.value?.route) {
    items.push({
      label: sourceBreadcrumb.value.labelKey ? t(sourceBreadcrumb.value.labelKey) : sourceBreadcrumb.value.label || '',
      route: sourceBreadcrumb.value.route,
    })
  }

  items.push({
    label: t(
      currentKeyword.value ? NEWS_SEARCH_BREADCRUMB_LABEL_KEY : currentCategoryBreadcrumbLabelKey.value || '85428',
    ),
  })
  return items
})

const newsListCanonicalUrl = useSeoCanonicalUrl()
const newsListJsonLd = computed(() => {
  if (is404.value) {
    return null
  }

  const pageUrl = newsListCanonicalUrl.value
  const siteUrl = new URL(pageUrl).origin
  const itemListId = `${pageUrl}#itemlist`
  const listDescription = t(
    currentKeyword.value ? '84121' : NEWS_CATEGORY_SEO_DESCRIPTION_KEYS[currentRouteCategory.value] || '84121',
  )
  const listItems = pageArticles.value
    .slice(0, 5)
    .filter((article) => article.title && article.urlPath)
    .map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: article.title,
      url: resolveSeoUrl(localePath(getNewsDetailPath(article.belongTabType, article.urlPath)), pageUrl),
    }))
    .filter((item) => item.url)
  const breadcrumb = createSeoBreadcrumbList(
    breadcrumbItems.value.map((item) => ({
      label: item.label,
      route: item.route,
    })),
    pageUrl,
  )
  const listName = t(
    currentKeyword.value
      ? NEWS_SEARCH_SEO_TITLE_KEY
      : NEWS_CATEGORY_SEO_TITLE_KEYS[currentRouteCategory.value] || NEWS_SEARCH_SEO_TITLE_KEY,
  )

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: listName,
        description: listDescription,
        inLanguage: localeMeta.language,
        isPartOf: createSeoWebSite(siteUrl, true),
        mainEntity: {
          '@id': itemListId,
        },
      },
      {
        '@type': 'ItemList',
        '@id': itemListId,
        name: listName,
        description: listDescription,
        inLanguage: localeMeta.language,
        itemListOrder: 'https://schema.org/ItemListOrderDescending',
        numberOfItems: listItems.length,
        itemListElement: listItems,
      },
      breadcrumb,
    ],
  }
})
useSeoJsonLd(newsListJsonLd, 'news-list-json-ld')

onMounted(() => {
  sourceBreadcrumb.value = getBreadcrumb('news-list')
})
const hotNewsItems = computed(() => (hotNewsResult.value?.items || []).slice(0, 6).map(toDisplayArticle))

watch(
  currentKeyword,
  (keyword) => {
    searchKeyword.value = keyword
  },
  { immediate: true },
)

/** 切换分类时进入对应的静态 SEO 页面。 */
const handleCategoryChange = async (category: NewsCategory) => {
  if (category === currentCategory.value) return

  isNewsSwitching.value = true
  const targetPath = currentKeyword.value ? '/news/search' : NEWS_CATEGORY_PATHS[category] || '/news'
  const query = currentKeyword.value ? { [NEWS_SEARCH_QUERY_KEY]: currentKeyword.value } : undefined

  await navigateTo({ path: localePath(targetPath), query })
}

/** 提交搜索后回到当前分类的第一页，空搜索词则回到普通列表。 */
const handleSearch = async () => {
  isNewsSwitching.value = true
  const keyword = searchKeyword.value.trim()
  const targetPath = keyword ? '/news/search' : NEWS_CATEGORY_PATHS[currentCategory.value] || '/news'
  const query = keyword ? { [NEWS_SEARCH_QUERY_KEY]: keyword } : undefined

  await navigateTo({ path: localePath(targetPath), query })
}

/** 清除搜索框文本，保持与反馈列表页一致，不立即触发搜索请求。 */
const clearSearch = () => {
  searchKeyword.value = ''
}

/** 安全转义搜索结果，防止 mock 或后续接口内容直接注入 HTML。 */
const escapeHtml = (text: string) =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

/** 按完整搜索词高亮标题和摘要，行为与反馈列表一致。 */
const getHighlightedText = (text: string) => {
  const escapedText = escapeHtml(text)
  const keyword = currentKeyword.value.trim()

  if (!keyword) return escapedText

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return escapedText.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="news-search-highlight">$1</mark>')
}
</script>

<template>
  <HeaderWikiBitPcTopNav :always-expand-search="true" :force-solid-background="true" />

  <CommonNotFound v-if="is404" />
  <main v-else class="min-h-screen bg-[#ffffff] text-[#292a2e]">
    <div class="mx-auto flex max-w-[1248px] flex-col px-[24px] pt-[70px] pb-[80px]">
      <CommonBreadcrumb :items="breadcrumbItems" class="news-list-breadcrumb" />
      <div class="grid grid-cols-[minmax(0,789px)_379px] gap-[24px] max-[1199px]:grid-cols-[minmax(0,1fr)]">
        <section class="min-w-0">
          <div
            v-if="currentKeyword || newsCategories.length > 1"
            class="sticky top-[70px] z-[3] mb-[24px] bg-[#ffffff]"
          >
            <nav
              class="flex min-w-0 flex-1 gap-[24px] border-b-[0.5px] border-[rgba(5,21,36,0.06)]"
              aria-label="新闻分类"
            >
              <template v-if="currentKeyword">
                <button
                  type="button"
                  class="flex h-[60px] shrink-0 cursor-pointer items-center text-[20px] leading-[28px] font-semibold text-[#292a2e]"
                  aria-current="page"
                >
                  {{ t('84608') }} {{ totalArticles }}
                </button>
              </template>
              <template v-else>
                <template v-for="category in newsCategories" :key="category.key">
                  <button
                    v-if="currentCategory === category.key"
                    type="button"
                    class="relative flex h-[60px] shrink-0 cursor-pointer items-center text-[20px] leading-[28px] font-semibold text-[#292a2e] after:absolute after:bottom-0 after:left-1/2 after:h-[4px] after:w-[28px] after:-translate-x-1/2 after:rounded-full after:bg-[#292a2e] after:content-['']"
                    aria-current="page"
                    @click="handleCategoryChange(category.key)"
                  >
                    {{ category.label }}
                  </button>
                  <button
                    v-else
                    type="button"
                    class="flex h-[60px] shrink-0 cursor-pointer items-center text-[20px] leading-[28px] font-normal text-[#7d818a] hover:text-[#292a2e]"
                    @click="handleCategoryChange(category.key)"
                  >
                    {{ category.label }}
                  </button>
                </template>
              </template>
            </nav>
          </div>

          <div
            v-if="articlePending || isNewsSwitching"
            class="flex flex-col"
            aria-busy="true"
            aria-label="加载新闻列表"
          >
            <div
              v-for="index in NEWS_PAGE_SIZE"
              :key="`news-skeleton-${index}`"
              class="grid grid-cols-[minmax(0,1fr)_192px] gap-[16px] border-b border-[rgba(5,21,36,0.06)] px-[16px] py-[24px]"
            >
              <div class="flex min-w-0 flex-col justify-between gap-[16px]">
                <div class="flex flex-col gap-[10px]">
                  <div class="h-[26px] w-[78%] animate-pulse rounded-[4px] bg-[#f1f2f4]" />
                  <div class="h-[44px] w-full animate-pulse rounded-[4px] bg-[#f1f2f4]" />
                </div>
                <div class="h-[18px] w-[28%] animate-pulse rounded-[4px] bg-[#f1f2f4]" />
              </div>
              <div class="h-[108px] animate-pulse rounded-[8px] bg-[#f1f2f4]" />
            </div>
          </div>
          <div v-else>
            <CommonEmptyState v-if="pageArticles.length === 0" />
            <template v-else>
              <article
                v-for="article in pageArticles"
                :key="article.id"
                class="cursor-pointer border-b-[0.5px] border-[rgba(5,21,36,0.06)] px-[16px] transition-colors last:border-b-0 hover:rounded-[8px] hover:bg-[#f8f8f8]"
                @click="handleArticleClick(article)"
              >
                <div class="grid grid-cols-[minmax(0,1fr)_192px] gap-[16px] py-[24px]">
                  <div class="flex min-w-0 flex-col justify-between gap-[12px]">
                    <div class="flex min-w-0 flex-col gap-[8px]">
                      <h2
                        class="line-clamp-1 text-[18px] leading-[26px] font-semibold text-[#292a2e]"
                        v-html="getHighlightedText(article.title)"
                      ></h2>
                      <p
                        class="line-clamp-2 text-[14px] leading-[22px] text-[#505258]"
                        v-html="getHighlightedText(article.summary)"
                      ></p>
                    </div>
                    <div class="flex items-center gap-[8px] text-[12px] leading-[18px] text-[#7d818a]">
                      <span class="flex items-center gap-[4px]">
                        <span class="size-[14px] text-center text-[#7d818a]" aria-hidden="true">•</span>
                        {{ article.date }}
                      </span>
                      <span
                        class="h-[10px] border-l border-[#0b122824] rtl:border-r rtl:border-l-0"
                        aria-hidden="true"
                      ></span>
                      <span>{{ article.tag }}</span>
                    </div>
                  </div>
                  <div class="h-[108px] overflow-hidden rounded-[8px]">
                    <CommonImage
                      class="size-full object-cover"
                      :src="article.image"
                      width="192"
                      height="108"
                      loading="eager"
                      object-fit="cover"
                    />
                  </div>
                </div>
              </article>
            </template>
          </div>

          <div v-if="pageArticles.length > 0" class="mt-[36px] flex justify-center">
            <CommonPagination
              class="news-pagination"
              :current-page="currentPage"
              :total="totalArticles"
              :page-size="NEWS_PAGE_SIZE"
              :page-route="newsPageRoute"
              @click.capture="handleNewsPaginationClick"
            />
          </div>
        </section>

        <aside class="sticky top-[70px] flex flex-col gap-[24px] self-start max-[1199px]:static max-[1199px]:w-full">
          <div class="flex h-[60px] items-center bg-[#ffffff]">
            <form
              class="flex h-[46px] w-[379px] items-center gap-[4px] rounded-full bg-[#f8f8f8] px-[16px] py-[12px] max-[1199px]:w-full"
              @submit.prevent="handleSearch"
            >
              <svg class="size-[16px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M14.5833 14.5833L18.3333 18.3333M16.6667 8.75C16.6667 13.1223 13.1223 16.6667 8.75 16.6667C4.37775 16.6667 0.833332 13.1223 0.833332 8.75C0.833332 4.37775 4.37775 0.833333 8.75 0.833333C13.1223 0.833333 16.6667 4.37775 16.6667 8.75Z"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <label class="sr-only" for="news-search-input">搜索新闻</label>
              <input
                id="news-search-input"
                v-model="searchKeyword"
                class="min-w-0 flex-1 bg-transparent text-[14px] leading-[22px] text-[#292a2e] outline-none placeholder:text-[#7d818a]"
                type="text"
                autocomplete="off"
                :placeholder="t('85431')"
              />
              <button
                v-if="searchKeyword.trim()"
                type="button"
                class="flex size-[20px] shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full border-0 p-0 text-[#8c8f97] shadow-none transition-colors"
                aria-label="Clear search"
                @click="clearSearch"
              >
                <CommonImage
                  src="https://resources1.interface003.com/web30/2026/07/13/69df0e6d-0a9d-4a63-a745-007906bc6eec.svg"
                  alt=""
                  width="20"
                  height="20"
                  loading="eager"
                  object-fit="contain"
                  aria-hidden="true"
                />
              </button>
            </form>
          </div>

          <div
            class="flex flex-col gap-[16px] rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] pt-[8px] pb-[16px]"
          >
            <h2
              class="w-full border-b-[0.5px] border-[rgba(5,21,36,0.06)] py-[16px] text-[20px] leading-[28px] font-semibold text-[#292a2e]"
            >
              {{ t('85432') }}
            </h2>
            <CommonEmptyState v-if="hotNewsItems.length === 0" />
            <div v-else class="flex flex-col gap-[16px]">
              <template v-for="(article, index) in hotNewsItems" :key="`hot-${article.id}`">
                <article class="flex cursor-pointer flex-col gap-[12px]" @click="handleArticleClick(article)">
                  <h3 class="line-clamp-2 text-[14px] leading-[22px] font-medium text-[#505258]">
                    {{ article.title }}
                  </h3>
                  <div class="flex items-center justify-between rounded-[4px]">
                    <span class="flex items-center gap-[8px] text-[14px] leading-[22px] text-[#8c8f97]">
                      <CommonImage
                        class="size-[22px] rounded-full object-cover"
                        :src="article.icon"
                        alt="WikiBit"
                        width="22"
                        height="22"
                        loading="eager"
                        object-fit="cover"
                      />
                      {{ article.showname }}
                    </span>
                    <span class="text-[12px] leading-[18px] text-[#8c8f97]">{{ formatNewsTime(article) }}</span>
                  </div>
                </article>
                <div v-if="index < hotNewsItems.length - 1" class="h-0 border-t-[0.5px] border-[rgba(5,21,36,0.06)]" />
              </template>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </main>
</template>

<style scoped lang="less">
:deep(.news-search-highlight) {
  background: transparent;
  color: #e2483d;
  font: inherit;
}

:deep(.news-pagination button[aria-current='page']) {
  background-color: #112d73;
  color: #ffffff;
}
</style>
